import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Link2Off, Settings } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { Domain } from '@/api/domainsApi';
import { pickVariant, formatPrimary } from './siteDomainStatusCard.utils';

export type SiteDomainStatusCardProps = {
  hasActiveSub: boolean;
  attachedCustomDomains: Domain[];
  availableVerifiedDomains: Domain[];
  canReadDomains: boolean;
  onUpgrade: () => void;
  onConnectClick: () => void;
  /** Called with the primary domain id when the user clicks Disconnect on the connected card. */
  onDisconnect: (domainId: string) => void;
  /** Whether a disconnect mutation is in flight (drives the button's loading state). */
  isDisconnecting?: boolean;
};

// Vertical layout: title, subcopy, then CTA stacked. Sized for the narrow
// 40% sidebar column the card now lives in.
const CARD_BASE =
  'rounded-2xl bg-foreground text-background p-6 flex flex-col gap-3 relative';

const TITLE = 'text-xl font-medium leading-tight';
const SUBCOPY = 'text-sm text-background/70';

// Reuse the existing pattern already used by the static "Upgrade" button on
// this card today: secondary variant with hover/active overrides so the pill
// reads as light-on-dark consistently.
const PILL_OVERRIDES = 'self-start hover:bg-surface active:bg-surface active:text-foreground';

export const SiteDomainStatusCard: React.FC<SiteDomainStatusCardProps> = (props) => {
  const variant = pickVariant({
    hasActiveSub: props.hasActiveSub,
    attachedCustomDomains: props.attachedCustomDomains,
    availableVerifiedDomains: props.availableVerifiedDomains,
    canReadDomains: props.canReadDomains,
  });

  // Connected state: render a finished/success card so the sidebar always
  // has something useful to read instead of disappearing once setup is
  // done. Confirms the primary domain and surfaces a Manage shortcut for
  // editing the connection later.
  if (variant === 'connected') {
    const primary = formatPrimary(props.attachedCustomDomains);
    // Card is dark-on-dark, so "primary" relative to the card surface is the
    // light pill (variant=secondary). Disconnect is the headline action and
    // gets that treatment; Manage domains drops to the tertiary inline-link
    // tone that reads quietly on the dark card.
    const flagged = props.attachedCustomDomains.find((d) => d.isPrimary);
    const primaryDomainId = flagged
      ? flagged.id
      : [...props.attachedCustomDomains].sort((a, b) =>
          a.createdAt.localeCompare(b.createdAt),
        )[0]?.id;
    return (
      <div className={CARD_BASE}>
        <span
          aria-hidden="true"
          className="absolute top-6 right-6 inline-flex h-8 w-8 items-center justify-center rounded-full bg-success/20 text-success"
        >
          <Check className="h-4 w-4 stroke-[2]" />
        </span>
        <h3 className={TITLE}>Domain connected</h3>
        {primary && (
          <p className="text-sm text-background break-all">
            <span className="font-medium">{primary.primary}</span>
            {primary.others > 0 && (
              <span className={SUBCOPY}> +{primary.others} more</span>
            )}
          </p>
        )}
        <p className={SUBCOPY}>
          Visitors land on your domain. Manage it any time from the Domains
          page.
        </p>
        <div className="flex items-center gap-2 mt-1">
          {primaryDomainId && (
            <Button
              variant="secondary"
              size="dense"
              className={PILL_OVERRIDES}
              onClick={() => props.onDisconnect(primaryDomainId)}
              disabled={props.isDisconnecting}
            >
              <Link2Off className="h-4 w-4 stroke-[1.5]" />
              {props.isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          )}
          <Button
            variant="tertiary"
            size="dense"
            className="bg-background/10 text-background hover:bg-background/15 active:bg-background/20"
            asChild
          >
            <Link to="/domains">
              <Settings className="h-4 w-4 stroke-[1.5]" />
              Manage domains
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={CARD_BASE}>
      <h3 className={TITLE}>Connect your domain</h3>
      {variant === 'free' && (
        <p className={SUBCOPY}>
          Upgrade to Pro to swap your subdomain for yourname.com. One-click DNS.
        </p>
      )}
      {variant === 'connect' && (
        <p className={SUBCOPY}>
          Pick from your verified domains and point this site at yourname.com.
        </p>
      )}
      {variant === 'add-domain' && (
        <p className={SUBCOPY}>Add a domain to point this site at yourname.com.</p>
      )}
      {variant === 'free' && (
        <Button
          variant="secondary"
          size="dense"
          className={PILL_OVERRIDES}
          onClick={props.onUpgrade}
        >
          Upgrade
          <ArrowRight className="h-4 w-4 stroke-[1.5]" />
        </Button>
      )}
      {variant === 'connect' && (
        <Button
          variant="secondary"
          size="dense"
          className={PILL_OVERRIDES}
          onClick={props.onConnectClick}
        >
          Connect
          <ArrowRight className="h-4 w-4 stroke-[1.5]" />
        </Button>
      )}
      {variant === 'add-domain' && (
        <Button variant="secondary" size="dense" className={PILL_OVERRIDES} asChild>
          <Link to="/domains?action=add">
            Add a domain
            <ArrowRight className="h-4 w-4 stroke-[1.5]" />
          </Link>
        </Button>
      )}
    </div>
  );
};

export default SiteDomainStatusCard;
